import React, { useState, useEffect } from 'react';
import { Check, Plus, AlertTriangle, X } from 'lucide-react';
import rentAiLogo from '../../assets/images/Black_RentAI_Vertical.svg';

const COMPARISON = [
  {
    area: 'Back office support',
    traditional: 'You manually manage tasks, updates, and follow-ups.',
    ai: 'Rentii helps you manage bookings, logistics, administration, operations, and resolution.',
  },
  {
    area: 'AI Agents',
    traditional: 'No awareness of your rental workflow.',
    ai: 'Rentii understands booking status, renter verification, operational tasks, logistics, and support needs before acting.',
  },
  {
    area: 'Smart Booking',
    traditional: 'You manage requests, pricing, availability, changes, and communication manually.',
    ai: 'Booking Agent helps manage requests, pricing context, trip changes, guest questions, and checkout workflows.',
  },
  {
    area: 'Logistical Tasks',
    traditional: 'You coordinate pickup, drop-off, delivery, and vehicle readiness outside the system.',
    ai: 'Logistics Agent helps coordinate trip timing, delivery, fleet readiness, availability, and location-based tasks.',
  },
  {
    area: 'Verification Tools',
    traditional: 'You may need separate tools for driver checks, renter review, and risk assessment.',
    ai: 'Verification Agent helps review renter status, license checks, identity signals, criminal check and risk context.',
  },
  {
    area: 'Admin Tasks',
    traditional: 'You manually track documents, notes, invoices, payment status, and customer records.',
    ai: 'Administrative Agent helps manage records, documents, payment context, invoices, policy references, and internal notes.',
  },
  {
    area: 'Operations',
    traditional: 'You rely on staff, spreadsheets, reminders, and manual coordination.',
    ai: 'Operations Agent helps prioritize tasks, alerts, reporting, reminders, messages, and workflow coordination.',
  },
  {
    area: 'Resolution Center',
    traditional: 'You manually handle tolls, tickets, violations, damage issues, disputes, and support cases.',
    ai: 'Resolution Agent helps manage resolution workflows on your behalf, with approval-based actions and your control.',
  },
  {
    area: 'Productivity',
    traditional: 'Your software stores information, but you still do most of the work.',
    ai: 'Rentii helps turn platform context into action, reducing overhead and keeping your back office moving.',
  },
];

const ComparisonPill = ({ area, open, onToggle }) => (
  <button
    type="button"
    onClick={onToggle}
    aria-expanded={open}
    className={`vipo-rentai__pill${open ? ' vipo-rentai__pill--open' : ''}`}
  >
    {!open && (
      <span className="vipo-rentai__pill-icon" aria-hidden="true">
        <Plus size={11} strokeWidth={2.5} />
      </span>
    )}
    {open && (
      <span className="vipo-rentai__pill-check" aria-hidden="true">
        <Check size={12} strokeWidth={3} />
      </span>
    )}
    <span className={`vipo-rentai__pill-label${open ? ' vipo-rentai__pill-label--open' : ''}`}>
      {area}
    </span>
  </button>
);

const RentAISection = () => {
  const [openIndex, setOpenIndex] = useState(0);
  const [modalOpen, setModalOpen] = useState(false);
  const selected = COMPARISON[openIndex];

  // Lock body scroll while the RentAI philosophy modal is open.
  useEffect(() => {
    document.body.style.overflow = modalOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [modalOpen]);

  return (
    <section className="vipo-section vipo-rentai">
      <div className="vipo-container vipo-container--narrow">
        <div className="vipo-rentai__header">
          <img
            src={rentAiLogo}
            alt="RentAI"
            className="vipo-rentai__logo"
          />
          <h2 className="vipo-rentai__title">You lead, Rentii acts.</h2>
          <p className="vipo-rentai__lead">
            Rentii is your context-aware AI agent built for the rental backoffice.
          </p>
          <p className="vipo-rentai__intro">
            Helping you reduce time-consuming back-office work, giving your team more time
            to focus on customers, vehicles, and revenue-generating activity.
          </p>
        </div>

        {/* Comparison — hidden on mobile (matches v0 hidden md:block) */}
        <div className="vipo-rentai__compare">
          <p className="vipo-rentai__compare-label">
            See how Rentid compares to traditional rental software.
          </p>

          <div className="vipo-rentai__cards">
            {/* Traditional card */}
            <div className="vipo-rentai__card vipo-rentai__card--gray">
              <div className="vipo-rentai__card-head">
                <p className="vipo-section__label vipo-rentai__card-label">
                  Traditional Software
                </p>
              </div>
              <div className="vipo-rentai__card-body">
                <AlertTriangle
                  className="vipo-rentai__card-icon vipo-rentai__card-icon--warn"
                  size={16}
                  strokeWidth={2.5}
                />
                <p className="vipo-rentai__card-copy vipo-rentai__card-copy--muted">
                  {selected.traditional}
                </p>
              </div>
            </div>

            {/* Rentid card with gradient stroke */}
            <div className="vipo-rentai__card-grad">
              <div className="vipo-rentai__card vipo-rentai__card--grad-inner">
                <div className="vipo-rentai__card-head">
                  <p className="vipo-section__label vipo-rentai__card-label">
                    Rentid Platform
                  </p>
                </div>
                <div className="vipo-rentai__card-body">
                  <span className="vipo-rentai__card-check">
                    <Check size={14} strokeWidth={3} />
                  </span>
                  <p className="vipo-rentai__card-copy vipo-rentai__card-copy--strong">
                    {selected.ai}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Pills — row 1: 4 pills, row 2: 5 pills */}
          <div className="vipo-rentai__pills-wrap">
            <div className="vipo-rentai__pills-row vipo-rentai__pills-row--4">
              {COMPARISON.slice(0, 4).map(({ area }, i) => (
                <ComparisonPill
                  key={area}
                  area={area}
                  open={openIndex === i}
                  onToggle={() => setOpenIndex(i)}
                />
              ))}
            </div>
            <div className="vipo-rentai__pills-row vipo-rentai__pills-row--5">
              {COMPARISON.slice(4).map(({ area }, j) => {
                const i = j + 4;
                return (
                  <ComparisonPill
                    key={area}
                    area={area}
                    open={openIndex === i}
                    onToggle={() => setOpenIndex(i)}
                  />
                );
              })}
            </div>
          </div>

          <div className="vipo-rentai__ailink-wrap">
            <button
              type="button"
              onClick={() => setModalOpen(true)}
              className="vipo-textlink"
            >
              Learn more about RentAI&trade;. Where people and agents drive results together
              on one secure rental platform
            </button>
          </div>
        </div>
      </div>

      {/* RentAI philosophy modal */}
      {modalOpen && (
        <>
          <div
            className="vipo-modal__backdrop"
            onClick={() => setModalOpen(false)}
            aria-hidden="true"
          />
          <div
            role="dialog"
            aria-modal="true"
            aria-label="Rentid AI philosophy"
            className="vipo-modal"
          >
            <div className="vipo-modal__grip" />
            <div className="vipo-modal__head">
              <div>
                <p className="vipo-section__label vipo-modal__eyebrow">RentAI&trade;</p>
                <h3 className="vipo-modal__title">
                  Where people and agents drive results together on one secure rental
                  platform.
                </h3>
              </div>
              <button
                type="button"
                onClick={() => setModalOpen(false)}
                className="vipo-modal__close"
                aria-label="Close"
              >
                <X size={20} />
              </button>
            </div>
            <div className="vipo-modal__body">
              <p>
                Our AI assistance works with a human-in-the-loop approach, helping improve
                speed, accuracy, and efficiency while keeping important decisions in your
                hands. We believe operators should stay in control, especially when
                decisions impact renters, vehicles, claims, payments, or business outcomes.
              </p>
              <p>
                That's why Rentid does not rely on fully autonomous AI agents making
                final decisions on their own. Our approach is designed with safety,
                oversight, and accountability at the center, giving your team the
                confidence to use AI while knowing the final call stays with you.
              </p>
              <p className="vipo-modal__cta-line">
                Come scale with us at{' '}
                <a
                  href="https://rentid.io"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  rentid.io
                </a>
              </p>
            </div>
          </div>
        </>
      )}
    </section>
  );
};

export default RentAISection;
